import { HTTPError } from 'ky';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { Loop54Types } from '~/lib/data-contract';
import { getPopularEntities } from '~/services/loop54/operations';
import { Prefetch } from '~/services/types';
import { hours, seconds } from '../../../../shared/utils/helpers';
import { GetPopularEntitiesRequest } from '~/lib/data-contract/auto-generated/loop54-api-model';

const OPTIONS = {
    cacheTime: seconds(10), // Cache 10 seconds to avoid filling up cache with filter requests
    staleTime: hours(1),
};

export default function usePopularEntitiesByPurchase<SelectType = PopularEntitiesByPurchaseRequest>(
    queryKey: string,
    params: PopularEntitiesByPurchaseRequest | undefined,
    select?: (data: PopularEntitiesByPurchaseResponse) => SelectType,
) {
    const { isReady } = useRouter();

    return useQuery<PopularEntitiesByPurchaseResponse, HTTPError | Error, SelectType>(
        [queryKey, params],
        ({ signal }) => getPopularEntities(signal, params),
        {
            ...OPTIONS,
            enabled: !!params,
            select,
            retry: false,
            keepPreviousData: true,
            refetchOnMount: isReady ? 'always' : false, // The data change over time, so we need to refetch it every time the component is mounted, but only when router is ready or else we end up with double requests on initial hydration
            refetchOnWindowFocus: false, // Never refetch on window focus since that will cause the products to change position
        },
    );
}
export type PopularEntitiesByPurchaseRequest = GetPopularEntitiesRequest;
export type PopularEntitiesByPurchaseResponse =
    | Loop54Types.GetEntitiesByAttributeResponse
    | Loop54Types.GetEntitiesResponse;

type PrefetchEntities = Prefetch & {
    queryKey: string;
    params: GetPopularEntitiesRequest;
};

export function buildPreFetcherForPopularEntities({
    queryKey,
    queryClient,
    params,
}: PrefetchEntities) {
    return queryClient.fetchQuery(
        [queryKey, params],
        ({ signal }) => getPopularEntities(signal, params),
        OPTIONS,
    );
}
